import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { CategoryChip } from "./CategoryChip";
import { blogPostPath, formatBlogDate, readingTimeLabel } from "@/lib/blog";

/**
 * Liste sayfasının üstündeki "öne çıkan" düzeni:
 *  - Tek yazı varsa tam genişlik büyük kart
 *  - 2-3 yazı varsa: 1 büyük (sol) + 1-2 küçük (sağ)
 *  - Hiç yoksa null
 */
export function FeaturedGrid({ posts = [] }) {
  const { lang, t } = useLanguage();
  if (!posts || posts.length === 0) return null;

  const fallbackImg = "/images/og-image.png";
  const [main, ...rest] = posts;
  const sideOnes = rest.slice(0, 2);

  return (
    <section className="py-12 sm:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            {t("blog.featured")}
          </h2>
        </div>

        <div className={`grid gap-5 ${sideOnes.length > 0 ? "md:grid-cols-12" : "md:grid-cols-1"}`}>
          {/* Büyük kart */}
          <Link
            to={blogPostPath(lang, main.slug)}
            className={`group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-slate-300 hover:shadow-xl hover:-translate-y-1.5 transition-all flex flex-col ${
              sideOnes.length > 0 ? "md:col-span-8" : ""
            }`}
          >
            <div className="aspect-[16/9] md:aspect-[16/8] overflow-hidden bg-slate-100">
              <img
                src={main.featured_image || fallbackImg}
                alt={main.title}
                loading="eager"
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                onError={(e) => (e.currentTarget.src = fallbackImg)}
              />
            </div>
            <div className="p-6 sm:p-8">
              {main.category_name && (
                <div className="mb-3">
                  <CategoryChip
                    name={main.category_name}
                    slug={main.category_slug}
                    color={main.category_color}
                    lang={lang}
                    size="sm"
                    as="span"
                  />
                </div>
              )}
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 group-hover:text-[#22A7D1] transition-colors leading-tight">
                {main.title}
              </h3>
              {main.excerpt && (
                <p className="mt-3 text-base text-slate-500 line-clamp-2 leading-relaxed">
                  {main.excerpt}
                </p>
              )}
              <div className="mt-5 flex items-center gap-4 text-sm text-slate-500">
                <span>{formatBlogDate(main.published_at, lang)}</span>
                {main.reading_time && (
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {readingTimeLabel(main.reading_time, lang)}
                  </span>
                )}
              </div>
            </div>
          </Link>

          {/* Yan kartlar */}
          {sideOnes.length > 0 && (
            <div className="md:col-span-4 flex flex-col gap-5">
              {sideOnes.map((p) => (
                <Link
                  key={p.id}
                  to={blogPostPath(lang, p.slug)}
                  className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-slate-300 hover:shadow-xl hover:-translate-y-1.5 transition-all flex-1 flex flex-col"
                >
                  <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                    <img
                      src={p.featured_image || fallbackImg}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                      onError={(e) => (e.currentTarget.src = fallbackImg)}
                    />
                  </div>
                  <div className="p-4 sm:p-5 flex-1 flex flex-col">
                    {p.category_name && (
                      <div className="mb-2">
                        <CategoryChip
                          name={p.category_name}
                          slug={p.category_slug}
                          color={p.category_color}
                          lang={lang}
                          size="sm"
                          as="span"
                        />
                      </div>
                    )}
                    <h4 className="text-base font-semibold text-slate-900 line-clamp-2 group-hover:text-[#22A7D1] transition-colors">
                      {p.title}
                    </h4>
                    <p className="mt-auto pt-3 text-xs text-slate-500">
                      {formatBlogDate(p.published_at, lang)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
